import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { rawApiClient } from '../services/api';

/**
 * Resolved client UI settings (Meeting 3 implementation).
 *
 * Fetched on mount via GET /api/client/ui-settings, kept in React context so
 * all components can read without re-fetching. Refetches when:
 *   - personaId changes
 *   - the window/tab regains focus (covers the "admin saved in another tab" case)
 *   - a CustomEvent('aimhi:clientUiSettingsChanged') is dispatched (admin can
 *     emit this after a save to force an immediate refresh in the same tab)
 *
 * See docs/canaday-youth-outreach/04-implementation-spec.md section 4.1.
 */

const FALLBACK = {
  roleLabel: 'AI Therapist',
  showNavEmojis: false,
  showPerBubbleAILabel: true,
  perBubbleAILabelStyle: 'full',
  aiAvatarDisplay: 'header',
  chatDisclaimerFooter: {
    enabled: true,
    text: 'AI assistant — your therapy is with your human therapist.'
  },
  focusModeEnabled: false,
  theme: {},
  aboutThisAi: {},
  dashboardWidgetCopy: {},
  fontScale: 'md',
  colorScheme: 'default',
  focusMode: false
};

const ClientUiContext = createContext(FALLBACK);

export function ClientUiProvider({ personaId, children }) {
  const [settings, setSettings] = useState(FALLBACK);
  // Local UI state for the user-toggleable focus mode (T3-3, partial — opt-in
  // hide-nav). Distinct from the platform/service-group `focusModeEnabled`
  // gate which controls whether the toggle is exposed to the user.
  const [focusMode, setFocusMode] = useState(false);

  const refetch = useCallback(async () => {
    // Skip fetch when there's no auth token — rawApiClient won't have the
    // x-auth-token header set yet, and an unauthenticated request would 401.
    const token = typeof window !== 'undefined' ? window.localStorage?.getItem('token') : null;
    if (!token) {
      setSettings(FALLBACK);
      return;
    }
    try {
      const res = await rawApiClient.get('/client/ui-settings', {
        params: personaId ? { personaId } : {},
        headers: { 'x-auth-token': token }
      });
      setSettings(res.data || FALLBACK);
    } catch (err) {
      // Don't break the UI if the endpoint is unreachable; fall back to safe defaults.
      console.warn('[useClientUiSettings] fetch failed, using fallback', err?.message);
      setSettings(FALLBACK);
    }
  }, [personaId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Refetch when the tab regains focus / becomes visible. Covers the case
  // where admin saved a change in another tab and the user comes back here.
  useEffect(() => {
    const onFocus = () => refetch();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    const onCustomChanged = () => refetch();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('aimhi:clientUiSettingsChanged', onCustomChanged);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('aimhi:clientUiSettingsChanged', onCustomChanged);
    };
  }, [refetch]);

  return (
    <ClientUiContext.Provider value={{ ...settings, focusMode, setFocusMode, refetch }}>
      {children}
    </ClientUiContext.Provider>
  );
}

export function useClientUiSettings() {
  return useContext(ClientUiContext);
}
